/**
 * StoryDirector Types
 *
 * Input/output schemas for the StoryDirector AI — the narrative engine
 * that generates psyche thoughts, faction reactions, rumors, and quest hooks.
 * Follows the same pattern as MobDirector (ai-types.ts / mob-director.ts).
 */
import { z } from 'zod';
import { AICallContextSchema, RumorCardSchema } from './ai-types';
import { PsycheConfigSchema, PsycheThoughtSchema } from './psyche-types';
import { FactionReputationSchema, CharacterIdeologySchema, FactionQuestHookSchema, IdeologyAxesSchema } from './faction-types';
import { CampaignProfileSchema } from './campaign-profile';
// =============================================================================
// REQUEST TYPES
// =============================================================================
export const StoryDirectorRequestTypeSchema = z.enum([
    'PSYCHE_THOUGHTS', // Generate internal voice thoughts for current moment
    'FACTION_REACTIONS', // How do factions react to recent events
    'RUMOR_GENERATION', // Generate rumors based on current state
    'QUEST_HOOKS', // Suggest faction-aware quest hooks
]);
// =============================================================================
// INPUT
// =============================================================================
export const StoryDirectorInputSchema = z.object({
    campaignId: z.string(),
    context: AICallContextSchema,
    // Current narrative state
    activeScene: z.string(), // Description of what's happening now
    recentEvents: z.array(z.string()).default([]), // Last 5-10 narrative beats
    // Character context (for psyche thoughts)
    characterContext: z.object({
        id: z.string(),
        name: z.string(),
        personalityTraits: z.string().optional(),
        ideals: z.string().optional(),
        bonds: z.string().optional(),
        flaws: z.string().optional(),
        background: z.string().optional(),
    }).optional(),
    // Faction state
    factionReputations: z.array(FactionReputationSchema).default([]),
    characterIdeologies: z.array(CharacterIdeologySchema).default([]),
    // Psyche config (for thought generation)
    psycheConfig: PsycheConfigSchema.optional(),
    // Campaign profile (for tone calibration)
    campaignProfile: CampaignProfileSchema.partial().optional(),
    // What we're asking for
    requestType: StoryDirectorRequestTypeSchema,
});
// =============================================================================
// OUTPUT
// =============================================================================
export const FactionReactionSchema = z.object({
    factionId: z.string(),
    reaction: z.string(), // Narrative description of reaction
    reputationDelta: z.number(), // How much rep changes
    reasoning: z.string(), // Why this faction reacts this way
    newQuestHook: FactionQuestHookSchema.optional(), // Emerging quest from this reaction
});
export const StoryDirectorOutputSchema = z.object({
    // Psyche thoughts for GM queue
    psycheThoughts: z.array(PsycheThoughtSchema).optional(),
    // Faction reactions to recent events
    factionReactions: z.array(FactionReactionSchema).optional(),
    // Rumors (reuses existing RumorCard type from ai-types)
    rumors: z.array(RumorCardSchema).optional(),
    // Meta-guidance for the GM
    narratorNote: z.string().optional(),
    // Suggested ideology shifts for characters based on their actions
    ideologyShifts: z.array(z.object({
        characterId: z.string(),
        shift: IdeologyAxesSchema,
        reason: z.string(),
    })).optional(),
});
//# sourceMappingURL=story-director-types.js.map